<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin_competitors.delete_modal.title") }}</template>
        <template v-slot:body>
            <div>
                <div class="mb-4 is-size-5" v-if="registeredCompetitor">
                    <p>
                        <span class="has-text-grey-light">{{ translate("page.start_fights.competitor") }}:</span>
                        <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                        <span class="clarity-mask">{{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }}</span>
                    </p>
                    <p>
                        <span class="has-text-grey-light">{{ translate("form.parameter_id") }}</span>
                        {{ registeredCompetitor.category }}
                    </p>
                </div>

                <p class="mb-4" v-if="registeredCompetitor">
                    {{ translate("admin_competitors.delete_modal.confirm_question") }}
                </p>

                <div v-if="loadingPreview" class="has-text-centered py-5">
                    <LoadingSpinner2 />
                    <p class="mt-3 has-text-grey-light">{{ translate("common.loading") }}</p>
                </div>

                <template v-else-if="preview">
                    <Notification v-if="preview.status === 'canDelete'" class="is-success is-light" :centered="false">
                        <strong>{{ translate("admin_competitors.delete_modal.safe_title") }}</strong>
                        <p class="mt-2">{{ translate("admin_competitors.delete_modal.safe_description") }}</p>
                    </Notification>

                    <Notification v-else-if="preview.status === 'canDeleteWithWarnings'" class="is-warning is-light" :centered="false">
                        <strong>{{ translate("admin_competitors.delete_modal.warning_title") }}</strong>
                        <p class="mt-2">{{ translate("admin_competitors.delete_modal.warning_description") }}</p>
                        <ul class="mt-3 ml-4" style="list-style: disc">
                            <li v-for="effect in preview.potentialSideEffects" :key="effect.parameterId + '-' + effect.type">
                                {{ describeEffect(effect.type) }}
                            </li>
                        </ul>
                        <p class="mt-3">{{ translate("admin_competitors.delete_modal.warning_repair") }}</p>
                    </Notification>

                    <Notification v-else-if="preview.status === 'cannotDelete'" class="is-danger is-light" :centered="false">
                        <strong>{{ translate("admin_competitors.delete_modal.blocked_title") }}</strong>
                        <p class="mt-2">{{ translate("admin_competitors.delete_modal.blocked_description") }}</p>
                        <ul class="mt-3 ml-4" style="list-style: disc">
                            <li v-for="effect in preview.potentialSideEffects" :key="effect.parameterId + '-' + effect.type">
                                {{ describeEffect(effect.type) }}
                            </li>
                        </ul>
                    </Notification>

                    <Notification v-else-if="preview.status === 'cannotDeletePaymentBlocked'" class="is-danger is-light" :centered="false">
                        <strong>{{ translate("admin_competitors.delete_modal.payment_blocked_title") }}</strong>
                        <p class="mt-2">
                            {{ translate("admin_competitors.delete_modal.payment_blocked_description") }}
                        </p>
                    </Notification>
                </template>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton
                    :is-loading="deletingInProgress"
                    class="button is-danger"
                    @click="confirmDelete"
                    :disabled="loadingPreview || !canDelete"
                >
                    {{ translate("common.delete") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Country from "../Common/Country.vue";
    import Notification from "../Common/Notification.vue";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import eventCompetitorsApi, { DeleteRegistrationPreviewResponse } from "../Api/eventCompetitors";
    import { CompetitorPaymentWithMetadata } from "../Api/payments";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { successToast, warningToast } from "../Common/toasts";
    import { eventBus, OPEN_SIDE_EFFECTS_REPORT_MODAL } from "./eventBus";
    import { EventRegistrationSideEffectReport, SideEffectType } from "../Common/types";

    const { modal, open: openModal, close } = useModal();
    const store = useRegisteredCompetitorsStore();

    const registeredCompetitor = ref<CompetitorPaymentWithMetadata | null>(null);
    const loadingPreview = ref(false);
    const deletingInProgress = ref(false);
    const preview = ref<DeleteRegistrationPreviewResponse | null>(null);

    const canDelete = computed(
        () => preview.value !== null && preview.value.status !== "cannotDelete" && preview.value.status !== "cannotDeletePaymentBlocked",
    );

    function open(competitor: CompetitorPaymentWithMetadata) {
        registeredCompetitor.value = competitor;
        preview.value = null;
        loadingPreview.value = true;
        openModal();

        eventCompetitorsApi
            .getDeletePreview(store.eventId, competitor.id)
            .then(response => {
                preview.value = response;
            })
            .catch(error => {
                close();
                handleError(error);
            })
            .finally(() => {
                loadingPreview.value = false;
            });
    }

    function confirmDelete() {
        if (!registeredCompetitor.value || !canDelete.value) {
            return;
        }

        deletingInProgress.value = true;

        return eventCompetitorsApi
            .deleteRegistration(store.eventId, registeredCompetitor.value.id)
            .then(response => {
                close();

                if (response.sideEffects.length === 0) {
                    successToast(translate("flash_messages.competitor_deleted"));
                } else {
                    warningToast(translate("side_effects_modal.event_registration.change_caused_side_effects"), 5000);
                    eventBus.$emit(OPEN_SIDE_EFFECTS_REPORT_MODAL, {
                        sideEffects: response.sideEffects,
                        eventId: store.eventId,
                        registeredCompetitor: registeredCompetitor.value!,
                    } satisfies EventRegistrationSideEffectReport);
                }

                return store.getRegisteredCompetitorsPage();
            })
            .catch(handleError)
            .finally(() => {
                deletingInProgress.value = false;
            });
    }

    function describeEffect(type: SideEffectType): string {
        return translate("side_effects_modal.event_registration.effect." + type);
    }

    defineExpose({ open, close, modal });
</script>
